import apiRetryService, { DEFAULT_RETRY_CONFIG } from "./apiRetryService";
import requestDeduplicationService from "./requestDeduplicationService";
import { debugError, debugGroup, debugLog } from "../utils/debugLog";
import { optimizeImageUrl } from "../utils/imageUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

export interface HomepageData {
  blog_posts: Array<{
    id: string;
    title: string;
    slug: string;
    excerpt: string;
    featured_image: string;
    category: string;
    tags?: string[];
    featured: boolean;
    publish_date: string;
    created_at: string;
    updated_at: string;
  }>;
  events: Array<{
    id: string;
    title: string;
    slug: string;
    description: string;
    featured_image: string;
    event_type: string;
    event_date: string;
    start_time: string;
    end_time: string;
    ends_next_day: boolean;
    location: string;
    price: string;
    ticket_url: string;
    featured: boolean;
    created_at: string;
    updated_at: string;
  }>;
  galleries: Array<{
    id: string;
    title: string;
    slug: string;
    description: string;
    cover_image: string;
    photo_count: number;
    featured: boolean;
    created_at: string;
    updated_at: string;
  }>;
  generated_at: string;
  cache_duration: number;
}

export interface HomepageDataResponse {
  success: boolean;
  data?: HomepageData;
  error?: string;
}

export interface HomepageDataParams {
  blog_count?: number;
  event_count?: number;
  gallery_count?: number;
  featured_only?: boolean;
}

class HomepageApiService {
  private async fetchFromAPI(params: HomepageDataParams = {}): Promise<HomepageDataResponse> {
    const url = `${API_BASE_URL}/homepage-data`;
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params.blog_count) queryParams.append('blog_count', params.blog_count.toString());
    if (params.event_count) queryParams.append('event_count', params.event_count.toString());
    if (params.gallery_count) queryParams.append('gallery_count', params.gallery_count.toString());
    if (params.featured_only) queryParams.append('featured_only', 'true');

    const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
    
    // Check if request should be deduplicated
    // DISABLED: Deduplication causes issues with Response stream consumption
    // if (requestDeduplicationService.shouldDeduplicate(queryKey, 'homepage-data')) {
    //   console.log('🔄 Homepage data request deduplicated');
    // }
    
    debugLog(`🏠 Fetching homepage data from: ${fullUrl}`);
    
    if (!API_KEY) {
      throw new Error("API_KEY is not configured in environment variables");
    }
    
    const startTime = Date.now();
    
    try {
      // Use executeWithRetry with UNIQUE metadata to prevent deduplication collisions
      const data = await apiRetryService.executeWithRetry(
        async () => {
          debugLog(`🔍 DEBUG: Starting fetch for ${fullUrl}`);
          const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'x-api-key': API_KEY,
            },
            mode: 'cors',
          });

          debugLog(`📡 Homepage API Response status: ${response.status}`);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          // Parse JSON within the retry function
          const jsonData = await response.json();
          debugGroup('🔍 DEBUG: Parsed JSON data', () => debugLog(jsonData));
          
          // Extract the actual data from the API response
          if (jsonData.success && jsonData.data) {
            return jsonData.data; // Return just the data part
          } else {
            throw new Error('API response missing data or unsuccessful');
          }
        },
        {
          ...DEFAULT_RETRY_CONFIG,
          maxAttempts: 4,
          baseDelay: 600,
        },
        // CRITICAL: Provide unique metadata to prevent collision with schedule API
        {
          method: 'GET',
          url: fullUrl,
          timestamp: Date.now(),
          attempt: 1
        }
      );
      
      // Record successful request performance
      const responseTime = Date.now() - startTime;
      requestDeduplicationService.recordCacheHit(responseTime);
      
      debugGroup('✅ Homepage API Response', () => debugLog(data));
      
      // Return in the expected HomepageDataResponse format
      // data is already the extracted inner data, so wrap it properly
      return {
        success: true,
        data: data
      };
    } catch (error) {
      // Record failed request performance
      const responseTime = Date.now() - startTime;
      requestDeduplicationService.recordCacheMiss(responseTime);
      
      debugError(`❌ Error fetching homepage data:`, error);
      
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        debugError('🚫 This is likely a CORS issue. Check:');
        debugLog('   1. API Gateway CORS configuration');
        debugLog('   2. API key authentication');
        debugLog('   3. Network connectivity');
      }
      
      // Return error in the expected HomepageDataResponse format
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async getHomepageData(params: HomepageDataParams = {}): Promise<HomepageDataResponse> {
    return this.fetchFromAPI(params);
  }

  // Utility method to get full image URL for different content types
  getImageUrl(filename: string, type: 'blog' | 'gallery' | 'event'): string {
    if (!filename) return '';

    const trimmed = filename.trim();
    if (!trimmed) return '';

    // If already a full URL (or a browser-managed scheme), don't try to rebuild it.
    if (/^(https?:)?\/\//i.test(trimmed) || /^(data:|blob:)/i.test(trimmed)) {
      return optimizeImageUrl(trimmed);
    }
    
    const baseUrl = 'https://station-public-images-prd.s3.eu-west-2.amazonaws.com';
    const folderMap = {
      'blog': 'blog',
      'event': 'events', 
      'gallery': 'galleries'
    };

    const normalizedPath = trimmed.replace(/^\/+/, '');

    // Some APIs return full keys (e.g. "eruption/events/foo.jpg") or folder-prefixed paths (e.g. "events/foo.jpg").
    if (normalizedPath.startsWith('eruption/')) {
      return optimizeImageUrl(`${baseUrl}/${normalizedPath}`);
    }

    const folder = folderMap[type];
    if (
      normalizedPath.startsWith(`${folder}/`) ||
      normalizedPath.startsWith('blog/') ||
      normalizedPath.startsWith('events/') ||
      normalizedPath.startsWith('galleries/')
    ) {
      return optimizeImageUrl(`${baseUrl}/eruption/${normalizedPath}`);
    }

    return optimizeImageUrl(`${baseUrl}/eruption/${folder}/${normalizedPath}`);
  }
}

export const homepageApiService = new HomepageApiService();
